/**
 * Live server-push events — the client half of `GET /api/events/ws` (§5, §6).
 *
 * Queue-backed work settles server-side long after the request that kicked it
 * off returned: a backtest finishes, a portfolio snapshot lands, a notification
 * is minted. Before this hook the operator had to refresh to see any of it. A
 * single always-mounted WebSocket now carries a tiny "something changed" signal
 * per completion and we translate each into a TanStack cache invalidation, so
 * the already-open view refetches itself. The socket is a CACHE-BUST channel,
 * not a data channel — the envelope names WHICH family moved, never the new
 * data; the authoritative read stays the normal REST query.
 *
 * CONTRACT (frozen, one JSON text frame per event):
 *   { v: 1, ts: number, event: LiveEvent }
 * Parsing is deliberately TOLERANT — an unknown `kind` or a bumped `v` from a
 * newer server is ignored, never thrown (see `parseLiveEvent`), so a server
 * that races ahead of this client degrades to "no refresh for that event"
 * instead of tearing down the socket.
 *
 * RESILIENCE, mirroring the focus-gated self-driven loop in `chat/resume.ts`:
 *  - Reconnect with exponential backoff (1s→2s→4s… capped 30s), reset to 1s on
 *    every successful OPEN.
 *  - VISIBILITY-GATED: a hidden tab burns no reconnect attempts (a backgrounded
 *    window can sit closed indefinitely); becoming visible resets the backoff
 *    and reconnects immediately. An already-open socket is left alone in the
 *    background — keepalive keeps it live and events keep arriving.
 *  - GAP RECOVERY: reconnecting after a drop can have missed events while
 *    down, so every OPEN *except the very first* invalidates all mapped
 *    families once. The first connect skips this — the route loaders already
 *    fetched fresh data on mount, so a blanket invalidation there is pure waste.
 *  - KEEPALIVE: a literal "ping" text frame every 30s so intermediaries don't
 *    idle-close the connection; the server's "pong" reply is ignored.
 *
 * Mounted ONCE, in `AppShell` — which `routes/__root.tsx` renders only for
 * authenticated routes, so the socket never opens on `/login` and tears down
 * cleanly on logout (the shell unmounts). No toasts in v1; the refresh is the
 * whole UX.
 *
 */
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { LiveEvent, LiveEventEnvelope } from "@richrich/contracts";
import { API_BASE } from "../../lib/api";
import { keys } from "../../lib/queryKeys";

export type { LiveEvent };

/** Backoff floor / ceiling and keepalive cadence. */
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const PING_MS = 30_000;

/** The known event kinds — anything else is a newer-server event we ignore. */
const KNOWN_KINDS: ReadonlySet<string> = new Set(["backtest", "snapshot", "notification"]);

/**
 * Same-origin WebSocket URL for the events endpoint, scheme derived from the
 * page (`wss:` under HTTPS, `ws:` otherwise). Reuses `API_BASE` so the `/api`
 * prefix stays defined in one place.
 */
export function liveEventsUrl(): string {
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}${API_BASE}/events/ws`;
}

/**
 * Tolerant parse of a raw text frame into a known `LiveEvent`, or `null` for
 * anything we don't handle: non-JSON, a version other than 1, a missing/foreign
 * `event.kind`. NEVER throws — a malformed or future-versioned frame must not
 * take down the socket.
 */
export function parseLiveEvent(raw: string): LiveEvent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const env = parsed as Partial<LiveEventEnvelope>;
  if (env.v !== 1) return null;
  const event = env.event;
  if (typeof event !== "object" || event === null) return null;
  const kind = (event as { kind?: unknown }).kind;
  if (typeof kind !== "string" || !KNOWN_KINDS.has(kind)) return null;
  return event as LiveEvent;
}

/** Invalidate the cache family a single event maps to. */
function invalidateForEvent(queryClient: QueryClient, event: LiveEvent): void {
  switch (event.kind) {
    case "backtest":
      // Backtest queries nest under ['strategies','detail',<strategyId>,…], so the
      // carried strategyId scopes the refresh to the one strategy that changed;
      // the list buckets refresh too (status badges). A frame from an older server
      // without strategyId falls back to the whole family.
      if (typeof event.strategyId === "string") {
        void queryClient.invalidateQueries({ queryKey: keys.strategies.detail(event.strategyId) });
        void queryClient.invalidateQueries({ queryKey: keys.strategies.listAll });
      } else {
        void queryClient.invalidateQueries({ queryKey: keys.strategies.all });
      }
      break;
    case "snapshot":
      void queryClient.invalidateQueries({ queryKey: keys.portfolio.all });
      break;
    case "notification":
      void queryClient.invalidateQueries({ queryKey: keys.notifications.list() });
      break;
  }
}

/** Invalidate every mapped family once — used for post-reconnect gap recovery. */
function invalidateAllFamilies(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: keys.strategies.all });
  void queryClient.invalidateQueries({ queryKey: keys.portfolio.all });
  void queryClient.invalidateQueries({ queryKey: keys.notifications.list() });
}

/**
 * Open and hold the live-events socket for the lifetime of the mounting
 * component (mount once, at the authenticated shell). Returns nothing — its
 * only effect is cache invalidation.
 */
export function useLiveEvents(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // One controller for the whole effect: aborting on unmount removes the
    // visibilitychange listener AND every socket listener ever registered, so
    // the teardown-triggered `close()` below can't fire a stray reconnect.
    const ac = new AbortController();
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let backoff = INITIAL_BACKOFF_MS;
    let everConnected = false; // gates gap-recovery invalidation to non-first opens
    let disposed = false;

    const clearReconnect = (): void => {
      if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    };
    const clearPing = (): void => {
      if (pingTimer !== undefined) clearInterval(pingTimer);
      pingTimer = undefined;
    };

    const scheduleReconnect = (): void => {
      if (disposed) return;
      // Hidden tabs don't retry — becoming visible drives an immediate reconnect.
      if (document.visibilityState === "hidden") return;
      clearReconnect();
      reconnectTimer = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    };

    function connect(): void {
      if (disposed || document.visibilityState === "hidden") return;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        return; // already live/pending — don't stack sockets
      }
      clearReconnect();
      const socket = new WebSocket(liveEventsUrl());
      ws = socket;
      const opts = { signal: ac.signal };

      socket.addEventListener(
        "open",
        () => {
          backoff = INITIAL_BACKOFF_MS; // successful open resets the ladder
          if (everConnected) invalidateAllFamilies(queryClient); // gap recovery
          everConnected = true;
          clearPing();
          pingTimer = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) socket.send("ping");
          }, PING_MS);
        },
        opts,
      );

      socket.addEventListener(
        "message",
        (ev: MessageEvent) => {
          if (typeof ev.data !== "string") return; // ignore any binary frame
          if (ev.data === "pong") return; // keepalive reply — nothing to do
          const event = parseLiveEvent(ev.data);
          if (event) invalidateForEvent(queryClient, event);
        },
        opts,
      );

      socket.addEventListener("error", () => socket.close(), opts); // → close → backoff

      socket.addEventListener(
        "close",
        () => {
          clearPing();
          if (ws === socket) ws = null;
          scheduleReconnect();
        },
        opts,
      );
    }

    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") {
        clearReconnect(); // stop retrying while backgrounded; keep an open socket
      } else {
        backoff = INITIAL_BACKOFF_MS;
        connect(); // resume immediately on return to foreground
      }
    };

    document.addEventListener("visibilitychange", onVisibility, { signal: ac.signal });
    connect();

    return () => {
      disposed = true;
      ac.abort(); // drops the visibility + all socket listeners in one shot
      clearReconnect();
      clearPing();
      ws?.close();
      ws = null;
    };
  }, [queryClient]);
}
